'use client';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getCookie } from 'cookies-next';
import { Controller, useForm } from 'react-hook-form';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import CreditCardIcons from '@/components/customercenter-ui/svgs/creditCards';
import MasonEasyPayCreditIcon from '@/components/customercenter-ui/svgs/masoneasypay-credit';
import MasseysCreditIcon from '@/components/customercenter-ui/svgs/masseys-credit';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import StoneberryCreditIcon from '@/components/customercenter-ui/svgs/stoneBerryCredit';
import ToolTips from '@/components/customercenter-ui/tooltip';
import SecurityCodeTooltip from '@/components/customercenter-ui/tooltip/securityCode';
import { showSoldAccountSection } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { SuccessNotification } from '@/helpers/utils/notification';
import { formatCurrency, getAgentName, getCookieValue, TranslateText } from '@/helpers/utils/siteUtils';
import {
  CardPayload,
  CustomerAddressPayload,
  PhoneRecordingPayload,
  TokenizeCardPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { ALL_PAYMENT_MEDIUM, COOKIE_KEYS, DEFAULT_SITE_LANG, MAX_PAGE_SIZE } from '../../../../../constants/constant';
import {
  CARD_NAMES,
  DOWN_PAYMENT_TRANSACTION_TYPE,
  PAYMENT_FORM_SPECS,
  PAYMENT_METHODS,
  RESPONSE_SUCCESS_MESSAGE,
} from '../../customer-detail/constants';
import { PageTexts } from '../../customer-detail/i18n';
import { paymentSchema } from '../../customer-detail/schema';
import { ShippingAddressDetailsType } from '../../customer-detail/types';
import {
  getCardType,
  handleCardNumberChange,
  transformShippingAddress,
  getBillingAddress,
} from '../../customer-detail/utils';
import { Button } from '../../ui/button';
import InputField from '../../ui/customInput/input';
import { RadioGroup, RadioGroupItem } from '../../ui/customRadio/customRadio';
import { CustomFloatingSelect } from '../../ui/customSelect/floatingSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';
import { maskPhoneNumber, maskPostalCode } from '../../utils/formatUtils';
import EditAddressModal from '../customerDetailAction/editAddress';
const DownPaymentModal = ({
  open,
  setOpen,
  order,
  billingAddress,
  primaryEmail,
  handleDownpaymentSuccess,
  isOrderDetails,
  isSoldAccount,
}: any) => {
  const { toast } = useToast();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [loading, setLoading] = useState(false);
  const { makePayment, tokenizeCard, getCustomerAddresses, startPhoneRecording, getCustomerDetails } = useAccount();
  const [addresses, setAddresses] = useState<ShippingAddressDetailsType[]>([billingAddress]);
  const [cardType, setCardType] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expDate, setExpDate] = useState('');
  const [data, setData] = useState<any>();
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<any>(billingAddress);
  const [groupValue, setGroupValue] = useState(PAYMENT_METHODS.minimumDue);
  const [otherAmount, setOtherAmount] = useState<string>('');
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState('');
  const customerId = data?.customerId ?? '';
  const divisionCode = data?.divisionCode ?? '';
  const downPaymentAmount = isOrderDetails
    ? order?.PostSubmissionInfo.downPaymentAmountRequested
    : order?.soaOrderDetails?.CreditStatus?.DownPaymentInfo?.downPaymentAmount ?? '0.0';
  const totalBalance = isOrderDetails ? order?.orderSummaryInfo.total : order?.total?.centAmount / 100;
  const orderId = isOrderDetails ? order?.orderId : order?.soaOrderDetails?.orderNumber ?? '';
  const projectSettings: ProjectSettings = useProjSettings();

  useEffect(() => {
    const cookieValue = getCookie(COOKIE_KEYS.CDP_URL ?? '{}') ?? '{}';
    const data = JSON.parse(cookieValue);
    setData(data);
  }, []);

  const startRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    await startPhoneRecording(payload);
  };

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    control,
    unregister,
  } = useForm({
    resolver: zodResolver(paymentSchema),
    mode: 'all',
  });

  const getTotalAmount = (paymentType: string) => {
    switch (paymentType) {
      case PAYMENT_METHODS.minimumDue:
        return downPaymentAmount ?? '0.0';
      case PAYMENT_METHODS.totalAmount:
        return totalBalance ?? '0.0';
      case PAYMENT_METHODS.otherAmount:
        return otherAmount;
      default:
        return '';
    }
  };

  const makeCardPayment = async (payload: CardPayload) => {
    await makePayment(payload).then((res) => {
      setLoading(false);
      if (res?.message === 'Successful') {
        setOpen(false);
        handleDownpaymentSuccess?.(true);
        startRecording();
        SuccessNotification(
          toast,
          {
            description: `Thank you for your payment. The payment confirmation number is ${res?.SubmitPaymentResults?.transactionId}`,
            action: <SuccessIcon className="h-5 w-5" />,
          },
          5000,
        );
      }
    });
  };
  const onSubmit = async (data: any) => {
    if (
      getTotalAmount(groupValue) === '0.0' ||
      getTotalAmount(groupValue) === '0' ||
      getTotalAmount(groupValue) === 0
    ) {
      return setError('Please set a valid payment amount');
    }
    setLoading(true);

    const CardInfo = {
      nameOnCard: selectedBillingAddress?.fullName,
      cardIssuerName: cardType,
      expirationMonth: data?.expDate?.split('/')[0],
      expirationYear: data?.expDate?.split('/')[1],
    };
    const payload = {
      query: {
        divisionCode: divisionCode,
      },
      data: {
        medium: ALL_PAYMENT_MEDIUM,
        CustomerInfo: {
          customerId: customerId,
          webSessionId: '',
          ipAddress: '',
          email: primaryEmail ?? '',
          phone: selectedBillingAddress?.phone,
        },
        BillingAddress: {
          line1: selectedBillingAddress?.streetName,
          line2: selectedBillingAddress?.additionalAddressInfo,
          city: selectedBillingAddress?.city,
          state: selectedBillingAddress?.state,
          zip: selectedBillingAddress?.postalCode,
          company: selectedBillingAddress?.company,
          country: selectedBillingAddress?.country,
        },
        CardInfo: {
          nameOnCard: CardInfo?.nameOnCard,
          cardIssuerName: cardType,
          pan: data?.cardNumber?.split(' ').join(''),
          expirationMonth: CardInfo?.expirationMonth,
          expirationYear: CardInfo?.expirationYear,
          securityCode: data?.cvv,
        },
      },
    } as TokenizeCardPayload;

    await tokenizeCard(payload).then((response) => {
      if (response?.message === 'Successful') {
        const PaymentPayload = {
          query: { ...payload?.query },
          data: {
            medium: ALL_PAYMENT_MEDIUM,
            transactionType: DOWN_PAYMENT_TRANSACTION_TYPE,
            chargeTotal: getTotalAmount(data?.paymentAmount),
            CustomerInfo: { ...payload?.data?.CustomerInfo },
            BillingAddress: { ...payload?.data?.BillingAddress },
            ApplyToOrders: [
              {
                orderId: orderId,
                paymentAmt: getTotalAmount(data?.paymentAmount),
              },
            ],
            CardInfo: {
              ...CardInfo,
              panTokenized: response?.TokenizeCardResults?.panTokenized,
            },
          },
        } as CardPayload;
        makeCardPayment(PaymentPayload);
      } else {
        setLoading(false);
      }
    });
  };

  const handleCardNumberInputChange = (e: any) => {
    handleCardNumberChange(e, setCardNumber);
    setCardType(getCardType(e.target.value));
  };
  const handleExpDateInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    let formattedValue = value.replace(/\D/g, '');
    if (formattedValue.length === 1 && parseInt(formattedValue) > 1) {
      formattedValue = '0' + formattedValue;
    } else if (formattedValue.length === 2 && parseInt(formattedValue) > 12) {
      formattedValue = '12';
    }
    setExpDate(formattedValue.slice(0, 2) + (formattedValue.length > 2 ? '/' + formattedValue.slice(2) : ''));
  };

  const handleChange = (id: string) => {
    const selectedBillingAddress = addresses?.filter((address: any) => address?.addressId === id);
    if (selectedBillingAddress?.length > 0) {
      setSelectedBillingAddress(selectedBillingAddress[0]);
    } else {
      setSelectedBillingAddress(billingAddress);
    }
  };
  const getShippingAddress = async () => {
    setLoading(true);
    const payload = {
      query: {
        omsCustomerId: customerId,
        pageSize: MAX_PAGE_SIZE,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as CustomerAddressPayload;
    await getCustomerAddresses(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const transformedAddress = transformShippingAddress(response?.RecipientAddresses || []);
        setAddresses([billingAddress, ...transformedAddress]);
      }
    });
    setLoading(false);
  };

  const getCustomerDetailsLocally = async () => {
    setLoading(true);
    try {
      const customerId = data?.customerId ?? '';
      const divisionCode = data?.divisionCode ?? '';
      const customerDetailsResponse = await getCustomerDetails({
        query: {
          customerId: customerId,
          divisionCode: divisionCode,
          creditAccountId: '',
          mailingRecordId: '',
          idName: 'customerId',
        },
      });
      if (customerDetailsResponse?.code === RESPONSE_SUCCESS_MESSAGE) {
        const billingAddress = getBillingAddress(customerDetailsResponse);
        setSelectedBillingAddress(billingAddress);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const getData = async () => {
      await getCustomerDetailsLocally();
      await getShippingAddress();
    };
    if (customerId && divisionCode) {
      getData();
    }
  }, [customerId, divisionCode]);

  useEffect(() => {
    if (open) {
      reset();
      setCardNumber('');
      setGroupValue(PAYMENT_METHODS.minimumDue);
      setExpDate('');
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) {
          startRecording();
        }
      }}
    >
      <DialogContent className="custom-scrollbar max-h-[90%] max-w-[640px] overflow-y-auto bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          {divisionCode === 'ZB' ? (
            <StoneberryCreditIcon />
          ) : divisionCode === 'ZY' ? (
            <MasseysCreditIcon />
          ) : divisionCode === 'ZD' ? (
            <MasonEasyPayCreditIcon />
          ) : (
            <StoneberryCreditIcon />
          )}

          <DialogTitle className="text-2xl font-bold">
            <section>
              <div className="mt-4 text-2xl font-bold">{Texts.makeDownPayment}</div>
              <div className="text-xs font-normal">{Texts.requiredFields}</div>
            </section>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <section className="mb-4">
            {showSoldAccountSection(projectSettings, isSoldAccount) ? (
              <section>
                <h1 className="mb-3 text-lg font-bold">{mapLocaleToMeaningfulFormat().downPaymentAmount}</h1>
                <Controller
                  name={'paymentAmount'}
                  control={control}
                  defaultValue={PAYMENT_METHODS.totalAmount}
                  render={({ field }) => (
                    <RadioGroup
                      defaultValue={PAYMENT_METHODS.totalAmount}
                      name={'paymentAmount'}
                      onValueChange={(value) => {
                        field.onChange(value);
                        setGroupValue(value);
                        setError('');
                        if (value !== PAYMENT_METHODS.otherAmount) {
                          unregister('additonalOtherAmount');
                        }
                      }}
                      value={groupValue}
                    >
                      <span className="mb-4 mr-4 flex items-center">
                        <Label htmlFor={mapLocaleToMeaningfulFormat().orderTotal}>
                          {mapLocaleToMeaningfulFormat().orderTotal}:
                          <span className="font-bold">
                            {''} {formatCurrency(totalBalance)}
                          </span>
                        </Label>
                      </span>
                    </RadioGroup>
                  )}
                />
                {error && <div className="font-medium text-scarletRed">{error}</div>}
              </section>
            ) : (
              <section>
                <h1 className="mb-3 text-lg font-bold">{mapLocaleToMeaningfulFormat().downPaymentAmount}</h1>
                <Controller
                  name={'paymentAmount'}
                  control={control}
                  defaultValue={PAYMENT_METHODS.minimumDue}
                  render={({ field }) => (
                    <RadioGroup
                      defaultValue={PAYMENT_METHODS.minimumDue}
                      name={'paymentAmount'}
                      onValueChange={(value) => {
                        field.onChange(value);
                        setGroupValue(value);
                        setError('');
                        if (value !== PAYMENT_METHODS.otherAmount) {
                          unregister('additonalOtherAmount');
                        }
                      }}
                      value={groupValue}
                    >
                      <span className="mb-4 mr-4 flex items-center">
                        <RadioGroupItem
                          value={PAYMENT_METHODS.minimumDue}
                          id={Texts.minimumDue}
                          className="mr-2 border-black"
                        />
                        <Label htmlFor={Texts.minimumDue}>
                          {Texts.minimumDue}:
                          <span className="font-bold">
                            {''} {formatCurrency(downPaymentAmount)}
                          </span>
                        </Label>
                      </span>
                      <span className="mb-4 mr-4 flex items-center">
                        <RadioGroupItem
                          value={PAYMENT_METHODS.totalAmount}
                          id={mapLocaleToMeaningfulFormat().orderTotal}
                          className="mr-2 border-black"
                        />
                        <Label htmlFor={mapLocaleToMeaningfulFormat().orderTotal}>
                          {mapLocaleToMeaningfulFormat().orderTotal}:
                          <span className="font-bold">
                            {''} {formatCurrency(totalBalance)}
                          </span>
                        </Label>
                      </span>
                      <span className="mb-4 mr-4 flex items-baseline">
                        <RadioGroupItem
                          value={PAYMENT_METHODS.otherAmount}
                          id={PAYMENT_METHODS.otherAmount}
                          className="mr-2 border-black"
                        />
                        <section className="w-[212px]">
                          <InputField
                            items={Texts.otherAmount}
                            classname="w-[212px] h-[54px]"
                            monetaryInput
                            rules={PAYMENT_METHODS.otherAmount ? { required: Texts?.makeAPayment } : ''}
                            register={groupValue === 'otherAmount' ? register : () => {}}
                            errors={groupValue === 'otherAmount' ? errors : {}}
                            label={'Other Amount'}
                            id={'additonalOtherAmount'}
                            htmlFor={'additonalOtherAmount'}
                            onChange={(e: any) => {
                              const amt = e.target.value;
                              const totalAmount =
                                parseInt(totalBalance) > parseInt(downPaymentAmount) ? totalBalance : downPaymentAmount;
                              if (amt <= totalAmount) {
                                setOtherAmount(e.target.value);
                              }
                            }}
                            maxLength={10}
                            value={otherAmount}
                            disabled={!(groupValue === 'otherAmount')}
                          />
                        </section>
                      </span>
                    </RadioGroup>
                  )}
                />
                {error && <div className="font-medium text-scarletRed">{error}</div>}
              </section>
            )}

            <h1 className="mb-3 text-lg font-bold">{Texts.creditTitle}</h1>
            <section className="flex w-full gap-4 px-1 pt-5">
              <section className="w-full max-w-[440px]">
                <InputField
                  items={PAYMENT_FORM_SPECS.cardNumber.placeHolder}
                  id={PAYMENT_FORM_SPECS.cardNumber.id}
                  htmlFor={PAYMENT_FORM_SPECS.cardNumber.id}
                  register={register}
                  errors={errors}
                  label={PAYMENT_FORM_SPECS.cardNumber.label}
                  classname="w-full"
                  value={cardNumber}
                  onChange={handleCardNumberInputChange}
                  cardType={cardType}
                  maxLength={cardType.toLowerCase() !== CARD_NAMES.AMERICAN_EXPRESS.toLowerCase() ? 19 : 18}
                />
              </section>
              <div className="gbomit pt-3">
                <CreditCardIcons />
              </div>
            </section>

            <section className="flex items-baseline gap-x-5 pl-1 pt-6">
              <section className="w-full max-w-[221px]">
                <InputField
                  items={PAYMENT_FORM_SPECS.expDate.placeHolder}
                  id={PAYMENT_FORM_SPECS.expDate.id}
                  htmlFor={PAYMENT_FORM_SPECS.expDate.id}
                  register={register}
                  errors={errors}
                  label={PAYMENT_FORM_SPECS.expDate.label}
                  classname="w-full"
                  value={expDate}
                  onChange={handleExpDateInputChange}
                  maxLength={5}
                />
              </section>
              <section className="w-full max-w-[173px]">
                <InputField
                  maxLength={cardType.toLowerCase() === CARD_NAMES.AMERICAN_EXPRESS ? 4 : 3}
                  items={PAYMENT_FORM_SPECS.cvv.placeHolder}
                  id={PAYMENT_FORM_SPECS.cvv.id}
                  htmlFor={PAYMENT_FORM_SPECS.cvv.id}
                  register={register}
                  errors={errors}
                  monetaryInput
                  label={PAYMENT_FORM_SPECS.cvv.label}
                  classname="w-full"
                />
              </section>

              <ToolTips Content={{ ContentToolTip: <SecurityCodeTooltip /> }} />
            </section>
          </section>
          <section>
            <h1 className="mb-4 text-lg font-bold">{Texts.billingAddress}</h1>
            <CustomFloatingSelect
              label={'billingAddress'}
              onChange={handleChange}
              id={'billingAddress'}
              register={register}
              setValue={setValue}
              errors={errors}
              classname={'w-[400px] ml-1'}
              placeholder={'*Select Address'}
              options={addresses?.map((items: any) => (
                <option key={items?.addressId} value={items?.addressId} className="px-2">
                  <p className="gbmask">{items?.fullName}</p>
                </option>
              ))}
            />
            <div className="ml-1 mt-2">
              {selectedBillingAddress !== undefined && (
                <section className="gbmask">
                  <p>{selectedBillingAddress?.fullName}</p>
                  <p>
                    {selectedBillingAddress?.streetName} {selectedBillingAddress?.additionalAddressInfo}
                  </p>
                  <p>
                    {selectedBillingAddress?.city} ,{selectedBillingAddress?.state}{' '}
                    {maskPostalCode(selectedBillingAddress?.postalCode ?? '')}
                  </p>
                  <p className="my-2">{maskPhoneNumber(selectedBillingAddress?.phone ?? '')}</p>
                </section>
              )}
              <button
                className="text-blueLight underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenModal(true);
                }}
                type="button"
              >
                {Texts.editAddress}
              </button>
              {openModal && (
                <EditAddressModal
                  Texts={Texts}
                  data={selectedBillingAddress}
                  setSelectedBillingAddress={setSelectedBillingAddress}
                  open={openModal}
                  setOpen={setOpenModal}
                  makeAPIcall={false}
                />
              )}
            </div>
          </section>

          <section className="mt-4 flex justify-start gap-4">
            <Button className="rounded-md bg-deepBlue p-4 text-white">{Texts.submitDownPayment}</Button>
            <Button
              className="p-0 underline"
              onClick={() => {
                reset();
                setOpen(false);
                startRecording();
              }}
              type="reset"
            >
              {Texts.cancelButton}
            </Button>
          </section>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DownPaymentModal;
